import { Severity, addBreadcrumb } from '@sentry/browser';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import {
  ContactFormFieldProps,
  ContactFormFunctionalityProps,
} from 'src/components/Contact/ContactFormTypes';
import { cookieNames } from 'src/constants/cookieNames';
import { trackEvent } from 'src/services/analytics/analytics';
import { retrieveBcReferralInfo } from 'src/services/growthapi';
import { readCookie } from 'src/utils/Cookies';
import { Environment } from 'src/utils/Environment';
import { SessionStorage } from 'src/utils/SessionStorage';
import { splitName } from 'src/utils/SplitName';
import * as Yup from 'yup';
import { useSalesChannel } from '../SalesChannel/SalesChannel';
import { ContactFormBody } from './ContactFormBody';
import { useContactForm } from './UseContactForm';

export const ContactForm: React.FC<ContactFormFieldProps & ContactFormFunctionalityProps> =
  ({
     apiClient = Environment.current.cosApiClient,
     onSuccess,
     onError,
     endpoint,
     ...formFieldProps
  }) => {

    const { getValidationSchema, getInitialValues } = useContactForm(formFieldProps);

    const { salesChannel } = useSalesChannel();
    const [initialValues, setInitialValues] = useState(getInitialValues());
    const [isLoadingReferralInfo, setIsLoadingReferralInfo] = useState(false);
    const [isFormPrefilled, setIsFormPrefilled] = useState(false);
    const [referralApiCalled, setReferralApiCalled] = useState(false);

    const fetchReferralData = () => {
      setReferralApiCalled(true);
      const userLinkToken = readCookie(cookieNames.INTEGRATION_TOKEN);
      if (!userLinkToken) return;
      setIsLoadingReferralInfo(true);
      retrieveBcReferralInfo(userLinkToken, true).then((data: any) => {
        trackEvent('BC Referral Token State', undefined, undefined, {
          isExpired: data.isTokenExpired,
        });
        if (!data || !data.lead || !data.business) {
          setIsLoadingReferralInfo(false);
          return;
        }
        const lead = data.lead;
        const names = splitName(lead.name, ' ');
        const business = data.business;
        const referralInfo = {
          firstName: names[0],
          lastName: names[1],
          businessName: business.name,
          emailAddress: lead.email,
          phoneNumber: lead.phoneNumber,
          address: business.address,
          businessAddressLine1: business.address && business.address.lineOne,
          addressLine2: business.address && business.address.lineTwo,
          city: business.address && business.address.city,
          state: business.address && business.address.state,
          zipCode: business.address && business.address.zipCode,
        };
        setInitialValues(referralInfo);
        setIsFormPrefilled(true);
        setIsLoadingReferralInfo(false);
      }).catch(err => {
        setIsLoadingReferralInfo(false);
        addBreadcrumb({
          category: 'Form prefill',
          message: 'Could not prefill account only form in BC referral flow',
          level: Severity.Error,
          data: err,
          type: 'error',
        });
      });
    };

    useEffect(() => {
      const referralType = readCookie(cookieNames.REFERRAL_TYPE);
      const formPath = window.location.pathname.indexOf('clover-account') !== -1 ? 'clover-account' : window.location.pathname;
      const formProperties = SessionStorage.tryGetItem(formPath);
      if (referralType !== 'BC' || referralApiCalled || !!formProperties) return;
      fetchReferralData();
    }, []);

    return (
      <Formik
        validateOnBlur
        validateOnChange
        validateOnMount
        initialValues={initialValues}
        enableReinitialize
        validationSchema={Yup.object().shape(getValidationSchema())}
        onSubmit={endpoint(onSuccess, onError, apiClient, salesChannel)}
      >
        {props => (
          <ContactFormBody
            {...props}
            isLoadingReferralInfo={isLoadingReferralInfo}
            isFormPrefilled={isFormPrefilled}
            fields={formFieldProps.formFields}
          />
        )}
      </Formik>
    );
  };
